import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Wrench, CheckCircle2, Circle } from 'lucide-react';

// Plan d'action : todos priorisées avec étapes structurées (cible + modification).

const PRIORITY_STYLE = {
  high: { dot: 'bg-red-500', label: 'Prioritaire' },
  medium: { dot: 'bg-amber-400', label: 'Moyen' },
  low: { dot: 'bg-blue-gray-300', label: 'Faible' },
};
const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

const CATEGORY_LABEL = {
  seo: 'SEO',
  performance: 'Performance',
  technique: 'Technique',
  accessibilite: 'Accessibilité',
  contenu: 'Contenu',
};

const EFFORT_LABEL = {
  faible: 'Effort faible',
  rapide: 'Effort faible',
  moyen: 'Effort moyen',
  eleve: 'Effort élevé',
  chantier: 'Effort élevé',
  variable: 'À évaluer',
};

/** Couleurs alignées sur effortInfo (rapide / moyen / chantier). */
const EFFORT_STYLE = {
  faible: 'text-green-700 bg-green-50 border-green-200',
  rapide: 'text-green-700 bg-green-50 border-green-200',
  moyen: 'text-amber-800 bg-amber-50 border-amber-200',
  eleve: 'text-red-700 bg-red-50 border-red-200',
  chantier: 'text-red-700 bg-red-50 border-red-200',
  variable: 'text-blue-gray-600 bg-blue-gray-50 border-blue-gray-200',
};

const EFFORT_ICON_COLOR = {
  faible: 'text-green-600',
  rapide: 'text-green-600',
  moyen: 'text-amber-600',
  eleve: 'text-red-600',
  chantier: 'text-red-600',
  variable: 'text-blue-gray-500',
};

/** Découpe un texte « action » legacy (liste numérotée en un seul paragraphe). */
function parseLegacyActionSteps(action) {
  if (!action || typeof action !== 'string') return [];
  const trimmed = action.trim();
  if (!trimmed) return [];

  const chunks = trimmed
    .split(/(?=\s*\d+[\.\)]\s+)/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (chunks.length <= 1 && !/^\d+[\.\)]/.test(trimmed)) {
    return [{ cible: '', modification: trimmed }];
  }

  return chunks.map((chunk) => {
    const m = chunk.match(/^\d+[\.\)]\s*(.*)$/s);
    const body = (m ? m[1] : chunk).trim();
    const colon = body.indexOf(' : ');
    if (colon > 0 && colon < 120) {
      return {
        cible: body.slice(0, colon).trim(),
        modification: body.slice(colon + 3).trim(),
      };
    }
    return { cible: '', modification: body };
  });
}

function resolveSteps(todo) {
  if (Array.isArray(todo.steps) && todo.steps.length > 0) {
    return todo.steps
      .map((s) => ({
        cible: String(s?.cible || s?.target || s?.element || '').trim(),
        modification: String(
          s?.modification || s?.change || s?.detail || s?.action || '',
        ).trim(),
      }))
      .filter((s) => s.cible || s.modification);
  }
  if (todo.action) return parseLegacyActionSteps(todo.action);
  return [];
}

function TodoMeta({ todo, stepsCount, open }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-blue-gray-500 shrink-0 pl-5 sm:pl-0 sm:ml-auto">
      {!open && stepsCount > 0 && (
        <span className="tabular-nums whitespace-nowrap">
          {stepsCount} étape{stepsCount > 1 ? 's' : ''}
        </span>
      )}
      {todo.category && CATEGORY_LABEL[todo.category] && (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-500 border border-neutral-200 rounded px-1.5 py-0.5 whitespace-nowrap">
          {CATEGORY_LABEL[todo.category]}
        </span>
      )}
      {todo.effort && EFFORT_LABEL[todo.effort] && (
        <span
          className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
            EFFORT_STYLE[todo.effort] || EFFORT_STYLE.variable
          }`}
        >
          <Wrench
            size={11}
            className={`shrink-0 ${EFFORT_ICON_COLOR[todo.effort] || EFFORT_ICON_COLOR.variable}`}
          />
          {EFFORT_LABEL[todo.effort]}
        </span>
      )}
    </div>
  );
}

function ActionSteps({ steps }) {
  if (!steps.length) return null;
  return (
    <ul className="space-y-3">
      {steps.map((step, idx) => (
        <li
          key={idx}
          className="pl-3 border-l-2 border-accent/40"
        >
          {step.cible && (
            <p className="text-sm font-medium text-blue-gray-900 leading-snug">
              {step.cible}
            </p>
          )}
          {step.modification && (
            <p className="text-sm text-blue-gray-600 leading-relaxed mt-1">
              {step.modification}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function TodoItem({ todo, done, onToggle }) {
  const prio = PRIORITY_STYLE[todo.priority] || PRIORITY_STYLE.medium;
  const steps = useMemo(() => resolveSteps(todo), [todo]);
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(todo.why || steps.length);

  const toggleOpen = () => {
    if (hasDetail) setOpen((v) => !v);
  };

  return (
    <div className={done ? 'bg-green-50/40' : 'bg-white'}>
      <div className="flex items-center gap-2.5 px-4 py-3 min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(todo._key);
          }}
          className="shrink-0 text-blue-gray-300 hover:text-green-500 transition"
          title={done ? 'Marquer comme à faire' : 'Marquer comme fait'}
        >
          {done ? (
            <CheckCircle2 size={18} className="text-green-500" />
          ) : (
            <Circle size={18} />
          )}
        </button>

        <button
          type="button"
          onClick={toggleOpen}
          disabled={!hasDetail}
          className={`min-w-0 flex-1 text-left flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3 ${
            hasDetail ? 'cursor-pointer' : 'cursor-default'
          }`}
          aria-expanded={hasDetail ? open : undefined}
        >
          <span className="flex items-start gap-2.5 min-w-0 flex-1">
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${prio.dot}`}
              title={prio.label}
            />
            <span
              className={`text-sm font-medium leading-snug min-w-0 ${
                done ? 'text-blue-gray-400 line-through' : 'text-blue-gray-900'
              }`}
            >
              {todo.title}
            </span>
          </span>
          <TodoMeta todo={todo} stepsCount={steps.length} open={open} />
        </button>

        {hasDetail && (
          <button
            type="button"
            onClick={toggleOpen}
            className="shrink-0 p-1 rounded hover:bg-neutral-50"
            aria-label={open ? 'Réduire' : 'Voir le détail'}
          >
            <ChevronDown
              size={16}
              className={`text-blue-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {open && hasDetail && (
        <div className="px-4 pb-4 pt-0">
          <div className="pl-5 border-l-2 border-accent/30 space-y-4">
            {todo.why && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-gray-500 mb-1.5">
                  Impact
                </p>
                <p className="text-sm text-blue-gray-600 leading-relaxed">{todo.why}</p>
              </div>
            )}
            {steps.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-gray-500 mb-2">
                  À faire ({steps.length})
                </p>
                <ActionSteps steps={steps} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {{ plan: object, storageKey: string, showSummary?: boolean }} props
 */
export default function IATodoList({ plan, storageKey, showSummary = true }) {
  const [completed, setCompleted] = useState(() => {
    try {
      const saved = storageKey ? localStorage.getItem(storageKey) : null;
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      const saved = storageKey ? localStorage.getItem(storageKey) : null;
      setCompleted(saved ? new Set(JSON.parse(saved)) : new Set());
    } catch {
      setCompleted(new Set());
    }
  }, [storageKey]);

  const todos = useMemo(() => {
    const list = Array.isArray(plan?.todos) ? plan.todos : [];
    return list
      .map((t, i) => ({ ...t, _key: `${t.title || 'todo'}-${i}` }))
      .sort(
        (a, b) =>
          (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1),
      );
  }, [plan]);

  const toggle = (key) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        if (storageKey) localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        // ignore
      }
      return next;
    });
  };

  if (!todos.length) {
    return (
      <p className="text-sm text-blue-gray-500">
        {plan?.summary || 'Aucune action prioritaire pour cette page.'}
      </p>
    );
  }

  const doneCount = todos.filter((t) => completed.has(t._key)).length;

  return (
    <div className="space-y-2">
      {showSummary && plan?.summary && (
        <p className="text-sm text-blue-gray-800 leading-relaxed bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3">
          {plan.summary}
        </p>
      )}
      {doneCount > 0 && (
        <p className="text-xs text-blue-gray-500">
          {doneCount}/{todos.length} action{todos.length > 1 ? 's' : ''} traitée
          {doneCount > 1 ? 's' : ''}
        </p>
      )}
      <div className="rounded-xl border border-neutral-200 overflow-hidden divide-y divide-neutral-100">
        {todos.map((t) => (
          <TodoItem
            key={t._key}
            todo={t}
            done={completed.has(t._key)}
            onToggle={toggle}
          />
        ))}
      </div>
    </div>
  );
}
